/**
 * RecommendationsSection - Seção de Recomendações no Perfil
 * 
 * Exibe recomendações de empregadores anteriores.
 * 
 * Tripé HeveaTalent:
 * - Justiça: Apenas recomendações positivas
 * - Segurança: Trabalhador controla visibilidade
 * - Simplicidade: Interface clara e direta
 * 
 * LGPD Compliant:
 * - Trabalhador consente ao aceitar
 * - Pode escolher identificado ou anônimo
 * - Pode remover a qualquer momento
 */

import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ThumbsUp, Eye, EyeOff, Trash2, CheckCircle, X, MessageSquare, Building2, User, Mail, Link, Copy, Clock, Send } from 'lucide-react';
import { useTranslation } from '../translations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper para headers de autenticação
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

/**
 * Componente para exibir recomendações públicas (Balcão de Empregos)
 */
export const RecommendationsDisplay = ({ workerId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/recommendations/worker/${workerId}`);
        if (response.ok) {
          const result = await response.json();
          setData(result);
        }
      } catch (error) {
        console.error('Erro ao carregar recomendações:', error);
      } finally {
        setLoading(false);
      }
    };

    if (workerId) {
      fetchRecommendations();
    }
  }, [workerId]);

  if (loading) {
    return (
      <div className="animate-pulse bg-gray-100 rounded-lg p-4">
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-3/4"></div>
      </div>
    );
  }

  if (!data || data.total_recommendations === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center gap-2 text-gray-500">
          <ThumbsUp className="w-5 h-5" />
          <span>{t('recNoRecommendations')}</span>
        </div>
        <p className="text-xs text-gray-400 mt-1">
          Recomendações são dadas por empregadores após o término do contrato
        </p>
      </div>
    );
  }

  return (
    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
      <div className="flex items-center gap-2 mb-3">
        <ThumbsUp className="w-5 h-5 text-green-600" />
        <span className="font-semibold text-green-800">
          {data.display_message}
        </span>
      </div>

      <div className="space-y-3">
        {data.recommendations.slice(0, 3).map((rec) => (
          <div key={rec.id} className="bg-white rounded-lg p-3 border border-green-100">
            {rec.message && (
              <p className="text-gray-700 text-sm italic mb-2">
                "{rec.message}"
              </p>
            )}
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center gap-1">
                {rec.is_example && (
                  <Badge className="bg-blue-100 text-blue-700 border border-blue-200 text-[10px] px-1.5 py-0">
                    {t('recExample')}
                  </Badge>
                )}
                {rec.employer_name ? (
                  <>
                    <Building2 className="w-3 h-3" />
                    <span>{rec.property_name || rec.employer_name}</span>
                  </>
                ) : (
                  <>
                    <User className="w-3 h-3" />
                    <span>{t('recIdentifiedRecommender')}</span>
                  </>
                )}
                {rec.verification_type === 'verified' ? (
                  <Badge className="ml-1 bg-green-100 text-green-700 border-green-200 text-[10px] px-1.5 py-0">
                    <CheckCircle className="w-2.5 h-2.5 mr-0.5" />{t('recVerified')}
                  </Badge>
                ) : (
                  <Badge className="ml-1 bg-gray-100 text-gray-500 border-gray-200 text-[10px] px-1.5 py-0">
                    <Link className="w-2.5 h-2.5 mr-0.5" />{t('recExternal')}
                  </Badge>
                )}
              </div>
              {rec.employment_period && (
                <span className="text-gray-400">{rec.employment_period}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {data.total_recommendations > 3 && (
        <p className="text-xs text-green-600 mt-2 text-center">
          +{data.total_recommendations - 3} outras recomendações
        </p>
      )}
    </div>
  );
};

/**
 * Componente para gerenciar recomendações (área do trabalhador)
 */
export const RecommendationsManager = ({ userId }) => {
  const [recommendations, setRecommendations] = useState({ accepted: [], pending: [] });
  const [invitations, setInvitations] = useState({ invitations: [], pending: 0 });
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const { t } = useTranslation();
  const [inviteForm, setInviteForm] = useState({ employer_name: '', employer_contact: '' });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(null);
  const [inviteError, setInviteError] = useState(null);

  const fetchRecommendations = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/recommendations/my`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setRecommendations({
          accepted: data.accepted || [],
          pending: data.pending || []
        });
      }
    } catch (error) {
      console.error('Erro ao carregar recomendações:', error);
    }
  };

  const fetchInvitations = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/invitations/my`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setInvitations(data);
      }
    } catch (error) {
      console.error('Erro ao carregar convites:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchRecommendations(), fetchInvitations()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleCreateInvite = async () => {
    if (!inviteForm.employer_name.trim()) {
      setInviteError(t('recEnterEmployerName'));
      return;
    }

    setInviteLoading(true);
    setInviteError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/invitations`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(inviteForm)
      });

      const data = await response.json();

      if (response.ok) {
        setInviteSuccess(data);
        await fetchInvitations();
        setInviteForm({ employer_name: '', employer_contact: '' });
      } else {
        setInviteError(data.detail || t('recErrorCreatingInvite'));
      }
    } catch (error) {
      setInviteError(t('recConnectionError'));
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCancelInvite = async (invitationId) => {
    if (!window.confirm(t('recCancelInvite'))) return;

    try {
      await fetch(`${BACKEND_URL}/api/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      await fetchInvitations();
    } catch (error) {
      console.error('Erro ao cancelar convite:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert(t('recLinkCopied'));
  };

  const handleRespond = async (recommendationId, action, visibility = 'identified') => {
    setProcessingId(recommendationId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/recommendations/respond`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          recommendation_id: recommendationId,
          action: action,
          visibility: visibility
        })
      });

      if (response.ok) {
        await fetchRecommendations();
      }
    } catch (error) {
      console.error('Erro ao processar recomendação:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleRemove = async (recommendationId) => {
    if (!window.confirm(t('recConfirmRemove'))) {
      return;
    }

    setProcessingId(recommendationId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/recommendations/${recommendationId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        await fetchRecommendations();
      }
    } catch (error) {
      console.error('Erro ao remover recomendação:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleToggleVisibility = async (recommendationId, currentVisibility) => {
    const newVisibility = currentVisibility === 'identified' ? 'anonymous' : 'identified';
    setProcessingId(recommendationId);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/recommendations/${recommendationId}/visibility?visibility=${newVisibility}`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        await fetchRecommendations();
      }
    } catch (error) {
      console.error('Erro ao alterar visibilidade:', error);
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-20 bg-gray-100 rounded"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <ThumbsUp className="w-5 h-5 text-green-600" />
        Minhas Recomendações
      </h3>

      {/* Recomendações - Todas aparecem automaticamente (não há mais pendentes) */}
      <div>
        <h4 className="text-sm font-medium text-green-600 mb-3">
          Recomendações no seu perfil ({recommendations.accepted.length})
        </h4>

        {recommendations.accepted.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500">
            <ThumbsUp className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p>{t('recNoRecommendationsYet')}</p>
            <p className="text-xs mt-1">
              Recomendações aparecem quando empregadores recomendam seu trabalho
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {recommendations.accepted.map((rec) => (
              <div key={rec.id} className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-medium text-gray-800 flex items-center gap-1">
                      <Eye className="w-4 h-4 text-green-600" />
                      {rec.employer_name}
                    </p>
                    {rec.property_name && (
                      <p className="text-sm text-gray-500">{rec.property_name}</p>
                    )}
                  </div>
                  {rec.verified_employment && (
                    <Badge className="bg-green-100 text-green-700 text-xs">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Verificado
                    </Badge>
                  )}
                </div>

                {rec.message && (
                  <p className="text-gray-700 italic text-sm">"{rec.message}"</p>
                )}

                {rec.employment_period && (
                  <p className="text-xs text-gray-400 mt-2">
                    Período: {rec.employment_period}
                  </p>
                )}

                <div className="flex gap-2 mt-2 flex-wrap">
                  {rec.is_example && (
                    <Badge className="bg-blue-100 text-blue-700 border border-blue-200 text-xs">
                      {t('recExample')}
                    </Badge>
                  )}
                  {rec.source === 'invitation' && (
                    <Badge className="bg-blue-50 text-blue-600 text-xs">
                      <Mail className="w-3 h-3 mr-1" />
                      {t('recViaInvite')}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Seção de Convites */}
      <div className="mt-6 pt-6 border-t">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Convites para Ex-Empregadores
          </h4>
          <Button
            size="sm"
            onClick={() => setShowInviteModal(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Send className="w-4 h-4 mr-1" />
            Convidar
          </Button>
        </div>

        {invitations.pending > 0 && (
          <div className="space-y-2 mb-4">
            {invitations.invitations
              .filter(inv => inv.status === 'pending')
              .map((inv) => (
                <div key={inv.id} className="flex items-center justify-between bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                  <div>
                    <p className="font-medium text-gray-800 text-sm">{inv.employer_name}</p>
                    <p className="text-xs text-yellow-600 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Aguardando resposta
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(`${window.location.origin}/recomendar/${inv.invite_code}`)}
                      title="Copiar link"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleCancelInvite(inv.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
          </div>
        )}

        {invitations.completed > 0 && (
          <p className="text-xs text-green-600">
            ✓ {invitations.completed} convite(s) respondido(s)
          </p>
        )}

        {invitations.pending === 0 && invitations.completed === 0 && (
          <p className="text-xs text-gray-400">
            Convide ex-empregadores que não usam a plataforma para recomendá-lo
          </p>
        )}
      </div>

      {/* Modal de Convite */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                Convidar Ex-Empregador
              </h3>
              <Button variant="ghost" size="sm" onClick={() => {
                setShowInviteModal(false);
                setInviteSuccess(null);
                setInviteError(null);
              }}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            {inviteSuccess ? (
              <div className="text-center py-4">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="font-medium text-gray-800 mb-2">{t('recInviteCreated')}</p>
                <p className="text-sm text-gray-600 mb-4">
                  Compartilhe o link com {inviteForm.employer_name || 'seu ex-empregador'}
                </p>
                <div className="bg-gray-100 rounded-lg p-3 mb-4">
                  <p className="text-xs text-gray-500 mb-1">{t('recInviteLink')}:</p>
                  <p className="text-sm font-mono break-all">{inviteSuccess.invite_url}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    className="flex-1"
                    onClick={() => copyToClipboard(inviteSuccess.invite_url)}
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copiar Link
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowInviteModal(false);
                      setInviteSuccess(null);
                    }}
                  >
                    {t('close')}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  {t('recCreateInviteDesc')}
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('recFormerEmployerName')} *
                    </label>
                    <input
                      type="text"
                      value={inviteForm.employer_name}
                      onChange={(e) => setInviteForm({...inviteForm, employer_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Ex: João da Silva"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('recEmailPhone')}
                    </label>
                    <input
                      type="text"
                      value={inviteForm.employer_contact}
                      onChange={(e) => setInviteForm({...inviteForm, employer_contact: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder={t('recForControl')}
                    />
                  </div>

                  {inviteError && (
                    <p className="text-sm text-red-600">{inviteError}</p>
                  )}

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setShowInviteModal(false)}
                    >
                      {t('cancel')}
                    </Button>
                    <Button
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      onClick={handleCreateInvite}
                      disabled={inviteLoading}
                    >
                      {inviteLoading ? t('recCreating') : t('recCreateInvite')}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Info sobre como funciona */}
      <div className="mt-4 p-3 bg-blue-50 rounded-lg text-xs text-blue-700">
        <p><strong>💡 Como funciona:</strong> Recomendações são dadas por empregadores e aparecem automaticamente no seu perfil público. Elas ajudam a demonstrar sua experiência e qualidade profissional.</p>
      </div>
    </Card>
  );
};

/**
 * Botão para empregador recomendar trabalhador
 */
export const RecommendButton = ({ workerId, workerName, onSuccess, currentUserType }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Profissionais não podem recomendar outros profissionais
  if (currentUserType === 'professional') {
    return null;
  }

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/recommendations`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          worker_id: workerId,
          message: message || null
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          setIsOpen(false);
          setSuccess(false);
          setMessage('');
          onSuccess?.();
        }, 2000);
      } else {
        setError(data.detail || 'Erro ao enviar recomendação');
      }
    } catch (err) {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="bg-green-600 hover:bg-green-700"
        data-testid="recommend-button"
      >
        <ThumbsUp className="w-4 h-4 mr-2" />
        Recomendar
      </Button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md p-6 bg-white">
        {success ? (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-green-700">
              Recomendação Enviada!
            </h3>
            <p className="text-gray-600 mt-2">
              {workerName} receberá sua recomendação e poderá aceitá-la.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <ThumbsUp className="w-5 h-5 text-green-600" />
                Recomendar {workerName}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MessageSquare className="w-4 h-4 inline mr-1" />
                  Deixe um elogio (opcional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Ex: Excelente profissional, muito dedicado e pontual."
                  maxLength={200}
                  rows={3}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">
                  {message.length}/200 caracteres
                </p>
              </div>

              {error && (
                <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
                <p className="font-medium mb-1">{t('recHowItWorks')}</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>{t('recAutoPublished')}</li>
                  <li>{t('recShowsOnProfile')}</li>
                  <li>{t('recNameAsReference')}</li>
                  <li>{t('recOnlyPositive')}</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setIsOpen(false)}
                  className="flex-1"
                >
                  {t('cancel')}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white" />
                  ) : (
                    <>
                      <ThumbsUp className="w-4 h-4 mr-2" />
                      {t('recSendRecommendation')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default RecommendationsDisplay;
